import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Zap, Play, Pause, Music2, Sparkles, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.2 + i * 0.13, duration: 0.8, ease: [0.34, 1.56, 0.64, 1] },
  }),
};

const floaties = [
  { Icon: Music2, cls: "top-[16%] left-[44%] text-pink", size: 30, delay: "0s" },
  { Icon: Music2, cls: "top-[40%] left-[1%] text-grape", size: 24, delay: "1.2s" },
  { Icon: Sparkles, cls: "top-[10%] right-[36%] text-tangerine", size: 26, delay: "2.1s" },
  { Icon: Heart, cls: "bottom-[22%] right-[3%] text-pink", size: 26, delay: "0.6s" },
  { Icon: Music2, cls: "bottom-[14%] left-[38%] text-mint", size: 22, delay: "1.7s" },
];

export default function Hero({ content, onOrder }) {
  const [name, setName] = useState("");
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCard = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);

  const headline = content?.hero_headline ?? "Turn Your Story Into a Custom Song";
  const words = headline.split(" ");
  const lines = [words.slice(0, 3).join(" "), words.slice(3).join(" ")].filter(Boolean);
  const subtitle = content?.hero_subtitle ?? "Recorded by real artists using your personal memories & inside jokes. Delivered to your inbox in 12 hours.";
  const audioSrc = content?.hero_audio_path ? `${API}/files/${content.hero_audio_path}` : "";
  const caption = (content?.hero_player_text || '"{name}, you are my home…"').replaceAll("{name}", name || "Ananya");

  const togglePlay = () => {
    if (audioSrc && audioRef.current) {
      if (playing) {
        audioRef.current.pause();
      } else {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    }
    setPlaying((p) => !p);
  };

  return (
    <section id="top" ref={ref} className="relative pt-28 sm:pt-36 pb-10 sm:pb-16 overflow-hidden" data-testid="hero-section">
      {floaties.map(({ Icon, cls, size, delay }, i) => (
        <span key={i} className={`float-note absolute ${cls} pointer-events-none hidden md:block`} style={{ animationDelay: delay }} aria-hidden="true">
          <Icon size={size} fill="currentColor" strokeWidth={1.5} />
        </span>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
        <div>
          <motion.div initial={{ opacity: 0, scale: 0.6, rotate: -10 }} animate={{ opacity: 1, scale: 1, rotate: -4 }} transition={{ type: "spring", bounce: 0.5 }} className="inline-block mb-6">
            <span className="sticker inline-block rounded-full bg-sunny border-2 border-ink font-display font-semibold text-xs uppercase tracking-wider px-4 py-1.5" data-testid="hero-offer-sticker">
              {content?.offer_badge ?? "50% OFF TODAY"}
            </span>
          </motion.div>

          <h1 className="font-display font-bold tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl" data-testid="hero-headline">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={reveal} initial="hidden" animate="show" className="block">
                  {i === lines.length - 1 ? (
                    <span className="relative inline-block text-pink">
                      {l}
                      <svg className="absolute -bottom-1 left-0 w-full" viewBox="0 0 220 12" fill="none" aria-hidden="true">
                        <motion.path d="M3 8c30-6 60 4 90-2s60 4 90-1 30-2 34 0" stroke="#FFC700" strokeWidth="4" strokeLinecap="round"
                          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 1.1, duration: 0.7, ease: "easeOut" }} />
                      </svg>
                    </span>
                  ) : l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-plum font-medium leading-relaxed"
            data-testid="hero-subcopy"
          >
            {subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
          >
            <input
              data-testid="hero-recipient-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Type their name — make them cry happy tears…"
              className="flex-1 rounded-full bg-white border-2 border-ink px-6 py-4 text-sm font-medium placeholder:text-lilac shadow-[4px_4px_0_#2D1A32] focus:outline-none focus:shadow-[4px_4px_0_#FF3B7E] transition-shadow"
            />
            <button
              data-testid="hero-cta-btn"
              onClick={() => onOrder(name ? { recipient_name: name } : {})}
              className="btn-pop rounded-full bg-pink text-white font-head font-bold px-8 py-4 text-sm border-2 border-ink shadow-[5px_5px_0_#2D1A32] whitespace-nowrap"
            >
              Order Your Song
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-3"
            data-testid="hero-trust-row"
          >
            <span className="sticker inline-flex items-center gap-1.5 rounded-full bg-white border-2 border-ink px-4 py-1.5 text-xs font-head font-semibold rotate-[1.5deg]">
              <Star size={13} className="text-sunny" fill="#FFC700" /> {content?.rating_text ?? "4.9/5 · 1,200+ happy tears"}
            </span>
            <span className="sticker inline-flex items-center gap-1.5 rounded-full bg-mint border-2 border-ink px-4 py-1.5 text-xs font-head font-semibold rotate-[-1.5deg]">
              <Zap size={13} /> 12-hour delivery
            </span>
          </motion.div>
        </div>

        <motion.div
          style={{ y: yCard }}
          initial={{ opacity: 0, scale: 0.9, rotate: 6 }}
          animate={{ opacity: 1, scale: 1, rotate: 2 }}
          transition={{ delay: 0.6, type: "spring", bounce: 0.4 }}
          className="flex justify-center mt-12 lg:mt-0"
        >
          <div className="relative w-full max-w-[380px] rounded-[2rem] bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-6 sm:p-8" data-testid="hero-vinyl-card">
            <span className="sticker absolute -top-4 -left-4 rounded-full bg-mint border-2 border-ink px-4 py-1.5 font-display font-semibold text-xs rotate-[-6deg]">100% Original</span>
            <span className="sticker absolute -bottom-4 -right-3 rounded-full bg-sunny border-2 border-ink px-4 py-1.5 font-display font-semibold text-xs rotate-[5deg]">Lyrics Included</span>
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-pink font-semibold">Now spinning</span>
              <span className="font-mono text-[10px] text-lilac">Side A · 33⅓</span>
            </div>
            <div className="relative mx-auto w-64 h-64">
              <div className={`w-full h-full rounded-full border-[3px] border-ink bg-[radial-gradient(circle,#5B3E66_30%,#2D1A32_31%,#3d2a44_32%,#2D1A32_50%,#3d2a44_51%,#2D1A32_70%)] flex items-center justify-center ${playing ? "vinyl-spin" : ""}`}>
                <div className="w-28 h-28 rounded-full bg-pink border-[3px] border-ink flex flex-col items-center justify-center text-center">
                  <span className="font-display font-bold text-white text-xl tracking-wide">VOWYO</span>
                </div>
              </div>
              <button
                data-testid="hero-vinyl-play-button"
                onClick={togglePlay}
                className="btn-pop absolute inset-0 m-auto w-14 h-14 rounded-full bg-sunny border-[3px] border-ink text-ink flex items-center justify-center shadow-[3px_3px_0_#2D1A32]"
                aria-label={playing ? "Pause preview" : "Play preview"}
              >
                {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
              </button>
            </div>
            {audioSrc && <audio ref={audioRef} src={audioSrc} onEnded={() => setPlaying(false)} preload="none" />}
            <div className="mt-6 flex items-end justify-center gap-1 h-8" aria-hidden="true">
              {[...Array(24)].map((_, i) => (
                <span key={i} className={`w-1.5 rounded-full ${i % 3 === 0 ? "bg-pink" : i % 3 === 1 ? "bg-sunny" : "bg-grape"} ${playing ? "eq-bar" : "scale-y-[0.3]"}`}
                  style={{ height: `${30 + 60 * Math.abs(Math.sin(i * 1.7))}%`, animationDelay: `${i * 0.07}s` }} />
              ))}
            </div>
            <p className="mt-4 text-center font-head font-semibold text-plum text-sm" data-testid="hero-player-caption">
              {caption}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
